import { z } from 'zod';

/** Pinned v4 PoolManager and HoodMorning CurrentPriceFeeHook events, not owner cash flows. */
export const canonicalSwapEventAbi = [
  { type: 'event', name: 'Swap', inputs: [
    { name: 'id', type: 'bytes32', indexed: true }, { name: 'sender', type: 'address', indexed: true },
    { name: 'amount0', type: 'int128', indexed: false }, { name: 'amount1', type: 'int128', indexed: false },
    { name: 'sqrtPriceX96', type: 'uint160', indexed: false }, { name: 'liquidity', type: 'uint128', indexed: false },
    { name: 'tick', type: 'int24', indexed: false }, { name: 'fee', type: 'uint24', indexed: false },
  ] },
  { type: 'event', name: 'QuoteFee', inputs: [
    { name: 'grossQuote', type: 'uint128', indexed: false }, { name: 'feeQuote', type: 'uint128', indexed: false },
    { name: 'preSwapValuation', type: 'uint256', indexed: false }, { name: 'hookFeeBps', type: 'uint16', indexed: false },
    { name: 'buying', type: 'bool', indexed: false },
  ] },
  // Signature coverage only: selecting the revised runtime's receipt ordering
  // still requires an explicit version-aware consumer, not historical replay.
  { type: 'event', name: 'BuyFeeRefund', inputs: [
    { name: 'swapCaller', type: 'address', indexed: true },
    { name: 'reservedFee', type: 'uint128', indexed: false },
    { name: 'earnedFee', type: 'uint128', indexed: false },
    { name: 'refundedFee', type: 'uint128', indexed: false },
  ] },
] as const;

const address = z.string().regex(/^0x[a-f0-9]{40}$/);
const hash = z.string().regex(/^0x[a-f0-9]{64}$/);
const inRange = (value: string, bits: number, positive = false) => value.length <= 78 && /^(0|[1-9][0-9]*)$/.test(value)
  && BigInt(value) < 2n ** BigInt(bits) && (!positive || BigInt(value) > 0n);
const uint = z.string().max(78).regex(/^(0|[1-9][0-9]*)$/).refine(value => inRange(value, 256));
const amount = uint.refine(value => inRange(value, 128));
const positiveAmount = amount.refine(value => inRange(value, 128, true));
export const swapProfileSchema = z.enum(['quote-before-swap-v1', 'quote-after-swap-refund-v2']);
export type SwapProfile = z.infer<typeof swapProfileSchema>;
export const canonicalSwapTradeSchema = z.strictObject({
  schemaVersion: z.literal(1), chainId: z.literal(4663), launchId: address, poolId: hash, manager: address, hook: address,
  transactionHash: hash, blockHash: hash, blockNumber: uint, logIndex: uint, feeLogIndex: uint,
  timestamp: z.iso.datetime(), observedAt: z.iso.datetime(), side: z.enum(['buy', 'sell']),
  baseAsset: address, quoteAsset: address, baseAmount: positiveAmount, poolQuoteAmount: positiveAmount,
  grossQuote: positiveAmount, hookFeeQuote: amount, executionQuote: positiveAmount,
  hookFeeBps: z.number().int().min(0).max(9999), venueFeePips: z.number().int().min(0).max(1000000),
  preSwapValuation: uint, sqrtPriceX96: uint.refine(value => inRange(value, 160, true)),
  managerSender: address, beneficialOwner: z.null(), attribution: z.literal('unknown'),
  amountScope: z.literal('canonical-pool-and-hook-not-vault-cashflow'),
  swapProfile: swapProfileSchema.nullable().default(null),
  buyFeeRefund: z.strictObject({ logIndex: uint, swapCaller: address, reservedFeeQuote: amount,
    earnedFeeQuote: amount, refundedFeeQuote: amount }).nullable().default(null),
}).superRefine((trade, context) => {
  if (![trade.grossQuote, trade.hookFeeQuote, trade.poolQuoteAmount, trade.executionQuote].every(value => inRange(value, 128))) return;
  const gross = BigInt(trade.grossQuote), fee = BigInt(trade.hookFeeQuote);
  if (fee !== gross * BigInt(trade.hookFeeBps) / 10000n
    || BigInt(trade.poolQuoteAmount) !== (trade.side === 'buy' ? gross - fee : gross)
    || BigInt(trade.executionQuote) !== (trade.side === 'buy' ? gross : gross - fee)
    || trade.baseAsset === trade.quoteAsset)
    context.addIssue({ code: 'custom', message: 'Canonical swap amount relationship mismatch' });
  const refund = trade.buyFeeRefund;
  const needsRefund = trade.swapProfile === 'quote-after-swap-refund-v2' && trade.side === 'buy';
  if (needsRefund !== (refund !== null)) context.addIssue({ code: 'custom', message: 'Swap profile refund evidence mismatch' });
  if (refund && [refund.reservedFeeQuote, refund.earnedFeeQuote, refund.refundedFeeQuote].every(value => inRange(value, 128))) {
    if (refund.swapCaller !== trade.managerSender || BigInt(refund.earnedFeeQuote) !== fee
      || BigInt(refund.reservedFeeQuote) !== BigInt(refund.earnedFeeQuote) + BigInt(refund.refundedFeeQuote))
      context.addIssue({ code: 'custom', message: 'Buy fee refund relationship mismatch' });
  }
  if (trade.swapProfile && [trade.logIndex, trade.feeLogIndex].every(value => inRange(value, 256))) {
    const feeBeforeSwap = trade.swapProfile === 'quote-before-swap-v1' && trade.side === 'buy';
    if (feeBeforeSwap ? BigInt(trade.feeLogIndex) >= BigInt(trade.logIndex) : BigInt(trade.feeLogIndex) <= BigInt(trade.logIndex))
      context.addIssue({ code: 'custom', message: 'Swap profile event ordering mismatch' });
    if (refund && inRange(refund.logIndex, 256) && BigInt(refund.logIndex) <= BigInt(trade.feeLogIndex))
      context.addIssue({ code: 'custom', message: 'Refund event precedes fee settlement' });
  }
});
export type CanonicalSwapTrade = z.infer<typeof canonicalSwapTradeSchema>;
